import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Check, Truck, RotateCcw, Minus, Plus, ArrowRight, PackageCheck } from "lucide-react";
import { fetchProduct, joinWaitlist } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { findStaticProduct } from "@/lib/products";
import ShipTodayCountdown from "@/components/ShipTodayCountdown";
import Seo from "@/components/Seo";
import Reviews from "@/components/Reviews";
import InstagramReelCard from "@/components/InstagramReelCard";
import { trackViewContent } from "@/lib/metaPixel";
import { getPromoPricing, getPromoBanner } from "@/lib/promo";

const Product = () => {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const { addItem } = useCart();
  const navigate = useNavigate();
  const staticP = findStaticProduct(slug);
  const [product, setProduct] = useState(staticP);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [variantId, setVariantId] = useState(staticP?.variants?.[0]?.id || null);
  const [loading, setLoading] = useState(!staticP);
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistDone, setWaitlistDone] = useState(false);

  useEffect(() => {
    const fallback = findStaticProduct(slug);
    setActiveImg(0);
    setQty(1);
    setWaitlistEmail("");
    setWaitlistDone(false);
    if (fallback) {
      setProduct(fallback);
      setVariantId(fallback.variants?.[0]?.id || null);
      setLoading(false);
    } else {
      setLoading(true);
    }
    fetchProduct(slug)
      .then((p) => {
        setProduct(p);
        setVariantId((curr) => curr || p.variants?.[0]?.id || null);
        setLoading(false);
      })
      .catch(() => {
        if (!fallback) {
          setLoading(false);
          navigate("/", { replace: true });
        }
      });
  }, [slug, navigate]);

  const variant = useMemo(
    () => product?.variants?.find((v) => v.id === variantId) || product?.variants?.[0],
    [product, variantId]
  );

  // Meta Pixel — ViewContent (fires once product+variant are resolved)
  useEffect(() => {
    if (!product || !variant) return;
    trackViewContent({
      id: `${product.id}::${variant.id}`,
      name: `${product?.name?.fr || product?.name?.en || product.slug} — ${variant?.name?.fr || variant?.name?.en || variant.id}`,
      price: variant.price,
      currency: "EUR",
    });
  }, [product?.id, variant?.id]);

  // Resolve gallery images: variant-specific images first, then common (always-visible) images.
  // Falls back to product.images if variant has no own gallery (older schema / cushion).
  const galleryImages = useMemo(() => {
    if (!product) return [];
    const variantImgs = variant?.images && variant.images.length > 0 ? variant.images : null;
    if (variantImgs) {
      const common = product.common_images || [];
      return [...variantImgs, ...common];
    }
    return product.images || [];
  }, [product, variant]);

  // Reset gallery cursor whenever variant changes
  useEffect(() => {
    setActiveImg(0);
  }, [variantId]);

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 md:px-16 py-24 text-center text-[#4a4a4a]">
        …
      </div>
    );
  }
  if (!product) return null;

  const outOfStock = variant?.in_stock === false;

  const handleAdd = () => {
    if (!variant || outOfStock) return;
    addItem(product, variant, qty);
    toast.success(
      lang === "fr"
        ? `${product.name.fr} — ${variant.name.fr} ajouté au panier`
        : `${product.name.en} — ${variant.name.en} added to cart`
    );
  };

  const handleWaitlist = async (e) => {
    e.preventDefault();
    if (!waitlistEmail || !variant) return;
    setWaitlistSubmitting(true);
    try {
      await joinWaitlist({
        email: waitlistEmail,
        product_id: product.id,
        variant_id: variant.id,
        lang,
      });
      setWaitlistDone(true);
      setWaitlistEmail("");
      toast.success(t.product.waitlistSuccess);
    } catch (_err) {
      toast.error(lang === "fr" ? "Email invalide" : "Invalid email");
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  const paragraphs = (product.description?.[lang] || "").split(/\n+/).filter(Boolean);

  return (
    <div data-testid="product-page" className="max-w-[1400px] mx-auto px-6 sm:px-10 md:px-16 py-10 md:py-16 pb-[120px] lg:pb-16">
      <Seo
        lang={lang}
        path={`/product/${product.slug}`}
        title={product.seo_title?.[lang] || `${product.name[lang]} — ${product.tagline[lang]}`}
        description={product.seo_description?.[lang] || product.short?.[lang] || product.tagline[lang]}
        image={galleryImages[0]}
      />
      {galleryImages[0] && (
        <Helmet>
          <link rel="preload" as="image" href={galleryImages[0]} fetchpriority="high" />
        </Helmet>
      )}
      {product.faq?.[lang]?.length > 0 && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: product.faq[lang].map((it) => ({
                "@type": "Question",
                name: it.q,
                acceptedAnswer: { "@type": "Answer", text: it.a },
              })),
            })}
          </script>
        </Helmet>
      )}
      <Link
        to="/"
        data-testid="product-back"
        className="inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase hover:opacity-60 transition-opacity mb-8 py-3 -my-1"
      >
        <ArrowLeft size={14} strokeWidth={1.4} />
        {t.product.back}
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        {/* Gallery */}
        <div className="lg:col-span-7">
          <div className="aspect-[4/5] overflow-hidden bg-[#ebe7df] rounded-sm">
            <img
              src={galleryImages[activeImg] || galleryImages[0]}
              alt={product.name[lang]}
              data-testid="product-main-image"
              width="1000"
              height="1250"
              decoding="async"
              fetchpriority="high"
              className="w-full h-full object-cover"
            />
          </div>
          {galleryImages.length > 1 && (
            <div className="mt-4 grid grid-cols-4 gap-3">
              {galleryImages.map((src, i) => (
                <button
                  type="button"
                  key={`${variant?.id || "default"}-${i}-${src}`}
                  onClick={() => setActiveImg(i)}
                  data-testid={`product-thumb-${i}`}
                  className={`aspect-square overflow-hidden bg-[#ebe7df] rounded-sm border ${
                    i === activeImg ? "border-[#1A1A1A]" : "border-transparent"
                  } transition-colors`}
                >
                  <img
                    src={src}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    width="200"
                    height="200"
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="lg:col-span-5 lg:pt-4">
          <div className="flex items-center gap-3 mb-4">
            <span className="eyebrow">myFLOVEA</span>
            {product.badge?.[lang] && (
              <span
                data-testid="product-badge"
                className="eyebrow bg-[#1A1A1A] !text-white px-3 py-1 rounded-full tracking-[0.18em]"
              >
                {product.badge[lang]}
              </span>
            )}
          </div>
          <h1
            data-testid="product-title"
            className="font-serif-display text-4xl md:text-5xl leading-[1.05] tracking-tight"
          >
            {product.name[lang]}
          </h1>
          {product.tagline?.[lang] && (
            <p
              data-testid="product-subtitle"
              className="mt-3 text-lg md:text-xl text-[#4a4a4a] italic font-normal"
            >
              {product.tagline[lang]}
            </p>
          )}
          {product.headline?.[lang] && (
            <p className="mt-5 text-[18px] md:text-[19px] text-[#1A1A1A] leading-snug font-medium">
              {product.headline[lang]}
            </p>
          )}

          <div className="mt-8 flex items-baseline gap-3 flex-wrap" data-testid="product-price-row">
            {(() => {
              const promo = getPromoPricing(product.id, variant?.id);
              if (promo) {
                return (
                  <>
                    <span
                      className="font-serif-display text-3xl text-[#B45C3F]"
                      data-testid="product-price"
                    >
                      {promo.promo.toFixed(2)} {t.common.currency}
                    </span>
                    <span
                      data-testid="product-compare-at-price"
                      className="text-lg text-[#8a8378] line-through"
                    >
                      {promo.base.toFixed(2)} {t.common.currency}
                    </span>
                    <span
                      data-testid="product-promo-badge"
                      className="text-[11px] tracking-[0.14em] uppercase bg-[#B45C3F] text-white px-2.5 py-1 rounded-full font-medium"
                    >
                      -{promo.discountPct}%
                    </span>
                  </>
                );
              }
              return (
                <>
                  <span className="font-serif-display text-3xl" data-testid="product-price">
                    {variant?.price.toFixed(2)} {t.common.currency}
                  </span>
                  {variant?.compare_at_price && variant.compare_at_price > variant.price && (
                    <span
                      data-testid="product-compare-at-price"
                      className="text-lg text-[#8a8378] line-through"
                    >
                      {variant.compare_at_price.toFixed(2)} {t.common.currency}
                    </span>
                  )}
                </>
              );
            })()}
            <span className="text-xs text-[#4a4a4a]">
              {t.common.ttc} · {t.product.freeShipping}
            </span>
          </div>
          {getPromoPricing(product.id, variant?.id) && (
            <div
              data-testid="product-promo-banner"
              className="mt-3 inline-flex items-center gap-2 bg-[#FCE9DC] text-[#8B3E1B] border border-[#EDCEB4] px-3 py-1.5 rounded-full text-[12px] font-medium"
            >
              🎁 {getPromoBanner(lang)}
            </div>
          )}
          {product.stock_delivery_badge?.[lang] && (
            <div
              data-testid="product-stock-delivery-badge"
              className="mt-4 inline-flex items-center gap-2 bg-[#3D2B1F] text-white px-4 py-2.5 rounded-md text-[13px] font-medium tracking-tight"
            >
              <PackageCheck size={15} strokeWidth={1.8} />
              {product.stock_delivery_badge[lang]}
            </div>
          )}
          <p
            data-testid="product-all-inclusive"
            className="mt-2 text-[13px] text-[#3D2B1F] font-medium"
          >
            {t.product.allInclusive}
          </p>

          {product.urgency?.[lang] && !product.stock_delivery_badge?.[lang] && (
            <div
              data-testid="product-urgency"
              className="mt-4 inline-flex items-center gap-2 bg-[#ebe7df] border border-[#dcd7ce] px-4 py-2.5 rounded-sm text-sm text-[#1A1A1A]"
            >
              {product.urgency[lang]}
            </div>
          )}

          {/* Fast-shipping reassurance badges (only when no stock_delivery_badge) */}
          {!product.stock_delivery_badge?.[lang] && (product.fast_ship_badge?.[lang] || product.stock_badge?.[lang] || product.delivery_time_badge?.[lang]) && (
            <div data-testid="product-reassurance" className="mt-5 flex flex-wrap gap-2">
              {product.fast_ship_badge?.[lang] && (
                <span
                  data-testid="reassurance-fast-ship"
                  className="inline-flex items-center gap-1.5 bg-[#3D2B1F] text-white px-3.5 py-2 rounded-full text-[12px] tracking-[0.12em] uppercase font-medium"
                >
                  🚚 {product.fast_ship_badge[lang]}
                </span>
              )}
              {product.delivery_time_badge?.[lang] && (
                <span
                  data-testid="reassurance-delivery-time"
                  className="inline-flex items-center gap-1.5 bg-[#F5EFE6] border border-[#dcd7ce] text-[#3D2B1F] px-3.5 py-2 rounded-full text-[12px] tracking-[0.12em] uppercase font-medium"
                >
                  ⏱️ {product.delivery_time_badge[lang]}
                </span>
              )}
              {product.stock_badge?.[lang] && (
                <span
                  data-testid="reassurance-stock"
                  className="inline-flex items-center gap-1.5 bg-[#EFE7DA] border border-[#dcd7ce] text-[#3D2B1F] px-3.5 py-2 rounded-full text-[12px] tracking-[0.12em] uppercase font-medium"
                >
                  ✅ {product.stock_badge[lang]}
                </span>
              )}
            </div>
          )}

          {/* Variant selector */}
          {product.variants && product.variants.length > 1 && (
            <div className="mt-8" data-testid="variant-selector">
              <div className="eyebrow mb-4">{t.product.chooseVariant}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {product.variants.map((v) => {
                  const active = v.id === variantId;
                  const promo = getPromoPricing(product.id, v.id);
                  return (
                    <button
                      type="button"
                      key={v.id}
                      onClick={() => setVariantId(v.id)}
                      data-testid={`variant-${v.id}`}
                      aria-pressed={active}
                      className={`text-left p-4 border rounded-sm transition-all relative ${
                        active
                          ? "border-[#1A1A1A] bg-[#1A1A1A] text-[#F5F0EB]"
                          : "border-[#dcd7ce] bg-transparent text-[#1A1A1A] hover:border-[#1A1A1A]"
                      }`}
                    >
                      {v.badge?.[lang] && (
                        <span
                          className={`absolute -top-2 -right-2 text-[10px] tracking-[0.12em] uppercase px-2 py-0.5 rounded-full ${
                            active
                              ? "bg-[#F5F0EB] text-[#1A1A1A]"
                              : "bg-[#1A1A1A] text-white"
                          }`}
                        >
                          {v.badge[lang]}
                        </span>
                      )}
                      <div className="flex items-baseline justify-between gap-3">
                        <div className="text-sm font-medium">{v.name[lang]}</div>
                        <div className="text-sm text-right leading-tight">
                          {promo ? (
                            <>
                              <span className="font-medium">{promo.promo.toFixed(2)} {t.common.currency}</span>
                              <span
                                className={`ml-2 line-through text-[11px] ${active ? "text-[#F5F0EB]/60" : "text-[#8a8378]"}`}
                                data-testid={`variant-${v.id}-compare`}
                              >
                                {promo.base.toFixed(2)}
                              </span>
                            </>
                          ) : (
                            <>{v.price.toFixed(0)} {t.common.currency}</>
                          )}
                        </div>
                      </div>
                      <div
                        className={`text-xs mt-1 leading-relaxed ${
                          active ? "text-[#F5F0EB]/80" : "text-[#4a4a4a]"
                        }`}
                      >
                        {v.desc[lang]}
                      </div>
                    </button>
                  );
                })}
              </div>
              {product.decision_aid?.[lang] && (
                <div
                  data-testid="decision-aid"
                  className="mt-4 text-sm text-[#1A1A1A] italic"
                >
                  {product.decision_aid[lang]}
                </div>
              )}
            </div>
          )}

          {/* Out-of-stock notice + waitlist (only when variant.in_stock === false) */}
          {outOfStock && (
            <div
              data-testid="out-of-stock-block"
              className="mt-8 border border-[#1A1A1A] bg-[#1A1A1A] text-[#F5F0EB] p-6 rounded-sm"
            >
              <div className="flex items-center gap-3 mb-3">
                <span
                  data-testid="oos-badge"
                  className="eyebrow bg-[#F5F0EB] text-[#1A1A1A] px-3 py-1.5 rounded-full tracking-[0.18em]"
                >
                  {variant.badge?.[lang] || t.product.outOfStockCta}
                </span>
              </div>
              <div className="font-serif-display text-2xl leading-snug mb-2">
                {t.product.outOfStockTitle}
              </div>
              <p className="text-sm text-[#F5F0EB]/80 mb-5 leading-relaxed">
                {t.product.outOfStockMsg}
              </p>
              {waitlistDone ? (
                <div
                  data-testid="waitlist-success"
                  className="text-sm text-[#F5F0EB] border-t border-[#F5F0EB]/30 pt-4"
                >
                  ✓ {t.product.waitlistSuccess}
                </div>
              ) : (
                <form
                  onSubmit={handleWaitlist}
                  data-testid="waitlist-form"
                  className="flex items-stretch gap-2"
                >
                  <input
                    type="email"
                    required
                    value={waitlistEmail}
                    onChange={(e) => setWaitlistEmail(e.target.value)}
                    placeholder={t.product.waitlistEmailPlaceholder}
                    data-testid="waitlist-email"
                    className="flex-1 bg-transparent border border-[#F5F0EB]/40 focus:border-[#F5F0EB] outline-none px-4 py-3 text-[15px] placeholder:text-[#F5F0EB]/50 text-[#F5F0EB] rounded-sm"
                  />
                  <button
                    type="submit"
                    disabled={waitlistSubmitting}
                    data-testid="waitlist-submit"
                    className="inline-flex items-center gap-2 bg-[#F5F0EB] text-[#1A1A1A] px-5 py-3 text-xs tracking-[0.18em] uppercase hover:bg-white disabled:opacity-60 transition-colors rounded-sm"
                  >
                    {t.product.waitlistSubmit}
                    <ArrowRight size={14} strokeWidth={1.4} />
                  </button>
                </form>
              )}
            </div>
          )}

          {/* Quantity + Add to cart */}
          <div className="mt-10 flex flex-col sm:flex-row gap-4 items-stretch">
            <div className="inline-flex items-center border border-[#dcd7ce] rounded-sm">
              <button
                type="button"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                data-testid="product-qty-decrement"
                disabled={outOfStock}
                className="w-12 h-12 flex items-center justify-center hover:bg-[#ebe7df] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Minus size={14} strokeWidth={1.4} />
              </button>
              <div className="w-12 text-center" data-testid="product-qty">
                {qty}
              </div>
              <button
                type="button"
                onClick={() => setQty((q) => q + 1)}
                data-testid="product-qty-increment"
                disabled={outOfStock}
                className="w-12 h-12 flex items-center justify-center hover:bg-[#ebe7df] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus size={14} strokeWidth={1.4} />
              </button>
            </div>
            <button
              type="button"
              onClick={handleAdd}
              disabled={outOfStock}
              data-testid="product-add-to-cart"
              aria-disabled={outOfStock}
              className={`group flex-1 px-9 py-5 text-sm tracking-[0.22em] uppercase font-medium rounded-md transition-all duration-300 ${
                outOfStock
                  ? "bg-[#dcd7ce] text-[#4a4a4a] cursor-not-allowed"
                  : "bg-[#3D2B1F] text-white hover:bg-[#1A1A1A] hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-18px_rgba(61,43,31,0.55)] shadow-[0_6px_20px_-10px_rgba(61,43,31,0.45)]"
              }`}
            >
              {outOfStock ? t.product.outOfStockCta : t.product.addToCart}
            </button>
          </div>

          {product.slug === "anneau-de-confort" && !outOfStock && (
            <ShipTodayCountdown lang={lang} />
          )}

          {/* Key points */}
          {product.key_points?.[lang]?.length > 0 && (
            <ul
              data-testid="product-key-points"
              className="mt-10 space-y-3 border-t border-[#dcd7ce] pt-8"
            >
              {product.key_points[lang].map((kp, i) => (
                <li key={i} className="text-[15px] leading-relaxed text-[#1A1A1A]">
                  {kp}
                </li>
              ))}
            </ul>
          )}

          {/* Shipping */}
          {product.shipping_info?.[lang] && (
            <div
              data-testid="product-shipping-details"
              className="mt-10 border border-[#dcd7ce] bg-[#ebe7df]/40 p-5 rounded-sm"
            >
              <div className="eyebrow mb-3">{t.product.shippingDetailsTitle}</div>
              <div className="flex items-start gap-3 text-[15px] text-[#1A1A1A] leading-relaxed">
                <Truck size={18} strokeWidth={1.4} className="mt-0.5 shrink-0" />
                <span>{product.shipping_info[lang]}</span>
              </div>
              <div
                data-testid="product-shipping-all-inclusive"
                className="mt-3 flex items-start gap-3 text-[14px] text-[#3D2B1F] font-medium"
              >
                <span>{t.product.allInclusive}</span>
              </div>
            </div>
          )}

          {/* Right of withdrawal — EU directive 2023/2673 */}
          <div className="mt-6">
            <Link
              to="/legal/retractation"
              data-testid="product-withdrawal-link"
              className="inline-flex items-center gap-2 border border-[#3D2B1F]/40 text-[#3D2B1F] px-5 py-3 text-xs tracking-[0.18em] uppercase rounded-md hover:bg-[#3D2B1F] hover:text-white transition-colors"
            >
              {t.product.exerciseWithdrawal}
            </Link>
          </div>

          {/* Power supply (cushion only) */}
          {product.power_supply?.[lang] && (
            <div
              data-testid="product-power-supply"
              className="mt-4 border border-[#dcd7ce] bg-[#F5F0EB] p-5 rounded-sm"
            >
              <div className="eyebrow mb-3">{t.product.powerSupplyTitle}</div>
              <p className="text-[15px] text-[#1A1A1A] leading-relaxed">
                {product.power_supply[lang]}
              </p>
            </div>
          )}

          {/* Certifications */}
          {product.certifications?.[lang] && (
            <div
              data-testid="product-certifications"
              className="mt-4 text-sm text-[#1A1A1A] leading-relaxed"
            >
              {product.certifications[lang]}
            </div>
          )}

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-start gap-3 text-sm">
              <Truck size={18} strokeWidth={1.4} className="mt-0.5 text-[#1A1A1A]" />
              <span className="text-[#4a4a4a]">{t.product.shipsFrom}</span>
            </div>
            <div className="flex items-start gap-3 text-sm">
              <RotateCcw size={18} strokeWidth={1.4} className="mt-0.5 text-[#1A1A1A]" />
              <span className="text-[#4a4a4a]">{t.product.satisfied}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="mt-20 max-w-3xl">
        <div className="eyebrow mb-5">{t.product.descriptionTitle}</div>
        <div className="space-y-5 text-[16px] md:text-[17px] text-[#1A1A1A] leading-relaxed">
          {paragraphs.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </div>

      {/* Instagram Reel — Coussin only */}
      {product.slug === "coussin-massant-chauffant" && (
        <div className="mt-20 md:mt-24">
          <InstagramReelCard
            thumbnail="/photos/coussin/coussin-siege-velour.webp"
            lang={lang}
          />
        </div>
      )}

      {/* Comparison table (anneau only) */}
      {product.comparison_table?.[lang] && (
        <section
          data-testid="comparison-table"
          className="mt-24 border border-[#dcd7ce] rounded-sm overflow-hidden"
        >
          <div className="px-6 md:px-10 py-8 border-b border-[#dcd7ce]">
            <div className="eyebrow mb-2">{lang === "fr" ? "Comparatif" : "Comparison"}</div>
            <h2 className="font-serif-display text-3xl md:text-4xl leading-tight tracking-tight">
              {t.product.comparisonTitle}
            </h2>
          </div>
          <div className="grid grid-cols-3 text-sm">
            <div className="bg-[#ebe7df] px-6 md:px-10 py-5 font-medium border-b border-[#dcd7ce]"></div>
            <div className="bg-[#1A1A1A] text-[#F5F0EB] px-6 md:px-10 py-5 font-medium border-b border-[#1A1A1A]">
              {product.comparison_table[lang].self_label}
            </div>
            <div className="bg-[#ebe7df] text-[#4a4a4a] px-6 md:px-10 py-5 border-b border-[#dcd7ce]">
              {product.comparison_table[lang].other_label}
            </div>
            {product.comparison_table[lang].rows.map((row, i) => (
              <div key={i} className="contents" data-testid={`comparison-row-${i}`}>
                <div className="px-6 md:px-10 py-4 border-b border-[#dcd7ce] font-medium text-[#1A1A1A]">
                  {row.feature}
                </div>
                <div className="px-6 md:px-10 py-4 border-b border-[#dcd7ce] bg-[#F5F0EB] text-[#1A1A1A] font-medium">
                  {row.self}
                </div>
                <div className="px-6 md:px-10 py-4 border-b border-[#dcd7ce] text-[#4a4a4a]">
                  {row.other}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Reviews */}
      {product.reviews?.length > 0 && (
        <section data-testid="reviews-section" className="mt-24">
          <div className="mb-10">
            <div className="eyebrow mb-2">{lang === "fr" ? "Avis clients" : "Customer reviews"}</div>
            <h2 className="font-serif-display text-3xl md:text-4xl leading-tight tracking-tight">
              {t.product.reviewsTitle} <span className="font-normal">❤️</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {product.reviews.map((r, i) => (
              <article
                key={i}
                data-testid={`review-card-${i}`}
                className="bg-[#F5F0EB] border border-[#dcd7ce] rounded-sm p-6 md:p-8"
              >
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  <span className="font-serif-display text-lg leading-none text-[#1A1A1A]">
                    {r.name}
                  </span>
                  {r.verified && (
                    <span className="text-[10px] tracking-[0.12em] uppercase bg-[#1A1A1A] text-[#F5F0EB] px-2 py-0.5 rounded-full">
                      {t.product.verifiedBadge} ✅
                    </span>
                  )}
                </div>
                <div
                  className="text-base text-[#C9A961] mb-4 tracking-[0.15em]"
                  aria-label={`${r.rating} sur 5`}
                  data-testid={`review-stars-${i}`}
                >
                  {"★".repeat(r.rating)}
                  <span className="text-[#dcd7ce]">{"★".repeat(5 - r.rating)}</span>
                </div>
                <p className="text-[15px] text-[#1A1A1A] leading-relaxed">
                  {r.text[lang]}
                </p>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* Dynamic (DB-driven) reviews + submission form */}
      <Reviews productId={product.slug} />

      {/* Product FAQ (SEO — FAQPage schema is emitted via Helmet above) */}
      {product.faq?.[lang]?.length > 0 && (
        <section
          data-testid="product-faq"
          className="mt-16 md:mt-24 max-w-3xl mx-auto"
        >
          <div className="text-center mb-8">
            <div className="eyebrow text-[#8B6343] mb-3">
              {lang === "fr" ? "Questions fréquentes" : "Frequently asked"}
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-[#1A1A1A]">
              {lang === "fr"
                ? "Tout ce que vous voulez savoir avant de commander"
                : "Everything you want to know before ordering"}
            </h2>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {product.faq[lang].map((item, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                data-testid={`product-faq-item-${i}`}
                className="border-b border-[#dcd7ce]"
              >
                <AccordionTrigger className="text-left font-serif-display text-lg md:text-xl py-5 hover:no-underline">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-[15px] leading-relaxed text-[#3D2B1F] pb-5">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      )}

      {/* Cross-sell */}
      {(product.cross_sell_slug || (product.cross_sell_slugs && product.cross_sell_slugs.length > 0)) && <CrossSell currentSlug={product.slug} />}

      {/* Sticky mobile add-to-cart */}
      <div
        data-testid="sticky-add-bar"
        className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-[#F5F0EB]/95 backdrop-blur border-t border-[#dcd7ce] px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex items-center gap-3"
      >
        <div className="flex-1 min-w-0">
          <div className="text-[11px] text-[#4a4a4a] truncate leading-tight">{product.name[lang]}</div>
          {(() => {
            const promo = getPromoPricing(product.id, variant?.id);
            if (promo) {
              return (
                <div className="font-serif-display text-lg leading-tight flex items-baseline gap-2">
                  <span className="text-[#B45C3F]">{promo.promo.toFixed(2)} {t.common.currency}</span>
                  <span className="text-[11px] text-[#8a8378] line-through">
                    {promo.base.toFixed(2)}
                  </span>
                </div>
              );
            }
            return (
              <div className="font-serif-display text-lg leading-tight">
                {variant?.price.toFixed(2)} {t.common.currency}
              </div>
            );
          })()}
        </div>
        <button
          type="button"
          onClick={handleAdd}
          disabled={outOfStock}
          data-testid="sticky-add-button"
          className={`px-6 py-4 text-xs tracking-[0.18em] uppercase font-medium rounded-md transition-all min-h-[52px] ${
            outOfStock
              ? "bg-[#dcd7ce] text-[#4a4a4a] cursor-not-allowed"
              : "bg-[#3D2B1F] text-white hover:bg-[#1A1A1A] active:translate-y-0.5"
          }`}
        >
          {outOfStock ? t.product.outOfStockCta : t.product.addToCart}
        </button>
      </div>
    </div>
  );
};

const CrossSell = ({ currentSlug }) => {
  const { t, lang } = useLang();
  const current = findStaticProduct(currentSlug);
  // Prefer the explicit `cross_sell_slugs` array; fall back to the legacy
  // singular `cross_sell_slug`; final fallback is the "other" hero product.
  const slugs = (current?.cross_sell_slugs && current.cross_sell_slugs.length > 0)
    ? current.cross_sell_slugs
    : current?.cross_sell_slug
      ? [current.cross_sell_slug]
      : [currentSlug === "coussin-massant-chauffant" ? "sphere-duo" : "coussin-massant-chauffant"];
  const targets = slugs.map(findStaticProduct).filter(Boolean);
  if (targets.length === 0) return null;
  return (
    <section data-testid="cross-sell" className="mt-24 border-t border-[#dcd7ce] pt-16">
      <div className="mb-8">
        <h2 className="font-serif-display text-3xl md:text-4xl leading-tight tracking-tight">
          {t.product.crossSellTitle}
        </h2>
      </div>
      <div className={`grid grid-cols-1 ${targets.length > 1 ? "md:grid-cols-2" : ""} gap-6 md:gap-8`}>
        {targets.map((target) => {
          const minPrice = target.variants?.length
            ? Math.min(...target.variants.map((v) => v.price))
            : target.base_price;
          const hasMultiPrice =
            target.variants?.length > 1 &&
            target.variants.some((v) => v.price !== target.variants[0].price);
          return (
            <Link
              key={target.slug}
              to={`/product/${target.slug}`}
              data-testid={`cross-sell-link-${target.slug}`}
              className="bg-[#F5F0EB] border border-[#dcd7ce] rounded-sm p-6 md:p-8 hover:border-[#1A1A1A] transition-colors group flex flex-col"
            >
              <div className="aspect-[4/3] bg-[#ebe7df] rounded-sm overflow-hidden mb-5">
                <img
                  src={target.images?.[0]}
                  alt={target.name[lang]}
                  loading="lazy"
                  decoding="async"
                  width="800"
                  height="600"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              {target.badge?.[lang] && (
                <span className="eyebrow bg-[#1A1A1A] !text-white px-3 py-1 rounded-full tracking-[0.18em] self-start mb-3">
                  {target.badge[lang]}
                </span>
              )}
              <h3 className="font-serif-display text-xl md:text-2xl leading-tight tracking-tight">
                {target.name[lang]}
              </h3>
              {target.tagline?.[lang] && (
                <p className="text-[#4a4a4a] mt-2 text-sm">{target.tagline[lang]}</p>
              )}
              <div className="mt-4 font-serif-display text-xl">
                {hasMultiPrice ? `${t.products.from} ` : ""}
                {minPrice.toFixed(2)} {t.common.currency}
              </div>
              <span className="mt-4 inline-flex items-center gap-2 text-[#1A1A1A] text-xs tracking-[0.18em] uppercase self-start group-hover:gap-3 transition-all">
                {t.product.crossSellCta}
                <ArrowRight size={14} strokeWidth={1.4} />
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

export default Product;
